import useSWR from 'swr'
import { m } from 'framer-motion';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import SvgIcon from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { List, ListItem, ListItemText } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import axiosInstance, { fetcher, endpoints } from 'src/utils/axios';

import { connection } from 'src/signalr'

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { varHover } from 'src/components/animate';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomTabs } from 'src/components/custom-tabs';
import { LoadingSpinner } from 'src/components/loading-screen/loading-spinner';


// ----------------------------------------------------------------------

const TABS = [
  { value: 'unread', label: 'Unread', count: 'unread' },
  { value: 'all', label: 'All', count: 'all' },
];

// ----------------------------------------------------------------------

export function NotificationsDrawer({ sx, ...other }) {

  const drawer = useBoolean();

  const { data = [], isLoading, mutate } = useSWR(
    endpoints.notifications,
    fetcher,
    {
      revalidateOnFocus: false,
    })

  const [currentTab, setCurrentTab] = useState('unread');

  useEffect(() => {
    const handler = () => mutate()

    connection.on("notification", handler);

    return () => {
      connection.off("notification", handler);
    };
  }, [mutate]);

  const handleChangeTab = useCallback((_, newValue) => setCurrentTab(newValue), []);

  const handleMarkAsRead = (notificationIds) =>
    axiosInstance
      .patch('api/notifications', notificationIds)
      .then(res => {
        mutate(prev => prev.map(n => notificationIds.includes(n.id) ? { ...n, isRead: true } : n))
      })
      .catch(err => console.error(err))

  const notifications = useMemo(() => ({
    unread: data.filter(n => !n.isRead),
    all: data,
  }), [data])

  const count = useMemo(() => ({
    unread: notifications.unread.length,
    all: notifications.all.length,
  }), [notifications])

  const filteredNotifications = currentTab === 'unread' ? notifications.unread : notifications.all

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!count.unread && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={() => handleMarkAsRead(notifications.unread.map(n => n.id))}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      <IconButton onClick={drawer.onFalse} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
        <Iconify icon="mingcute:close-line" />
      </IconButton>

      <IconButton>
        <Iconify icon="solar:settings-bold-duotone" />
      </IconButton>
    </Stack>
  );

  const renderTabs = (
    <CustomTabs variant="fullWidth" value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'unread' && 'info') ||
                'default'
              }
            >
              {count[tab.count]}
            </Label>
          }
        />
      ))}
    </CustomTabs>
  );

  const renderList = (
    <Scrollbar>
      <List>
        {filteredNotifications.map(({ id, title, description, isRead }) => (
          <ListItem
            key={id}
            divider
            onClick={() => handleMarkAsRead([id])}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <ListItemText primary={title} secondary={description} />

            {!isRead && <Box
              sx={{
                top: 26,
                width: 8,
                height: 8,
                right: 20,
                borderRadius: '50%',
                bgcolor: 'info.main',
                position: 'absolute',
              }}
            />}
          </ListItem>
        ))}
      </List>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={drawer.onTrue}
        sx={sx}
        {...other}
      >
        <Badge badgeContent={count.unread} color="error">
          <SvgIcon>
            {/* https://icon-sets.iconify.design/solar/bell-bing-bold-duotone/ */}
            <path
              fill="currentColor"
              d="M18.75 9v.704c0 .845.24 1.671.692 2.374l1.108 1.723c1.011 1.574.239 3.713-1.52 4.21a25.794 25.794 0 0 1-14.06 0c-1.759-.497-2.531-2.636-1.52-4.21l1.108-1.723a4.393 4.393 0 0 0 .693-2.374V9c0-3.866 3.022-7 6.749-7s6.75 3.134 6.75 7"
              opacity="0.5"
            />
            <path
              fill="currentColor"
              d="M12.75 6a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0zM7.243 18.545a5.002 5.002 0 0 0 9.513 0c-3.145.59-6.367.59-9.513 0"
            />
          </SvgIcon>
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 1, maxWidth: 420 } }}
      >
        {renderHead}

        {renderTabs}

        {isLoading ? <LoadingSpinner /> : renderList}

        <Box sx={{ p: 1 }}>
          <Button fullWidth size="large">
            View all
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
